/**
 * SGChairDetails Component - Steering Group Chair and Vice Chair Details Display - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * Displays chair and vice chair information for steering groups with profile images,
 * associated working groups navigation, and modal profile views. Features image loading
 * with fallback to initials, and dynamic group card display with click navigation.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.currentGroup - Current steering group data object
 * @param {string} props.currentGroup.workingGroupName - Name of the steering group
 * @returns {JSX.Element} The SGChairDetails component
 * 
 * @example
 * <SGChairDetails currentGroup={{workingGroupName: "Policy Steering Group"}} />
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ProfileModal from '../../modals/ProfileModal';
import apiHelper from '../../utils/apiHelper';
import { FILE_NAMES } from '../../utils/constants';

import './SGChairDetails.css';
import toast from 'react-hot-toast';
import { Toaster } from 'react-hot-toast';

/**
 * Generates initials from first and last names
 * 
 * @param {string} firstName - First name
 * @param {string} lastName - Last name
 * @returns {string} Uppercase initials (e.g., "JD" for John Doe)
 */
function getInitials(firstName, lastName) {
  return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
}

function SGChairDetails({ currentGroup }) {

  const navigate = useNavigate();
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [groupMembers, setGroupMembers] = useState({ chair: null, viceChair: null });
  const [associatedGroups, setAssociatedGroups] = useState([]);
  const [chairImage, setChairImage] = useState(null);
  const [viceChairImage, setViceChairImage] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Fetch group data and members on component mount
  useEffect(() => {
    /**
     * Fetches steering group member details and associated working groups
     * Filters members by group name and identifies chair/vice chair positions
     */
    const fetchData = async () => {
      try {
        const [memberResponse, wgDataResult] = await Promise.all([
          apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.WG_MEMBER_DETAILS),
          apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.WG_STRUCTURE)
        ]);

        if (memberResponse && Array.isArray(memberResponse.fileContent)) {
          const groupName = currentGroup.workingGroupName;
          
          const members = memberResponse.fileContent.filter(member => 
            member.workingGroupType === 'Steering Group' && member.workingGroupName === groupName
            
          );
          
          const chairMember = members.find(m => m.workingGroupPosition === "Chair");
          const viceChairMember = members.find(m => m.workingGroupPosition === "Vice Chair");

          setGroupMembers({
            chair: chairMember || null,
            viceChair: viceChairMember || null
          });
        }

        // Get associated working and project groups
        const associatedGroupsData = wgDataResult.fileContent.filter(
          group => group.steeringGroupName === currentGroup.workingGroupName 
          && group.workingGroupType !== "Steering Group"
        );

        setAssociatedGroups(associatedGroupsData);

      } catch (error) {
        toast.error('Failed to load data. Please try again.');
      }
    };

    fetchData();
  }, [currentGroup.workingGroupName, refreshTrigger]);

  // Load chair profile image
  useEffect(() => {
    if (groupMembers.chair?.email) {
        /**
         * Loads chair profile image from server with authenticated request
         * Creates blob URL for image display and handles errors gracefully
         */
        const loadImage = async () => {
            try {
                // First get the profile
                const profile = await apiHelper.getMemberProfileByEmail(groupMembers.chair.email);
                if (!profile?.photo) {
                    setChairImage(null);
                    return;
                }

                // Get the image using the photo filename
                const authToken = await apiHelper.getAuthToken();
                const response = await fetch(`${process.env.REACT_APP_API_URL}graph/getMediaContent/profile-images/${profile.photo}`, {
                    headers: {
                        'Authorization': `Bearer ${authToken}`
                    }
                });

                if (!response.ok) {
                    setChairImage(null);
                    return;
                }

                const blob = await response.blob();
                const imageUrl = URL.createObjectURL(blob);
                setChairImage(imageUrl);
            } catch (error) {
                setChairImage(null);
            }
        };
        loadImage();
    }

    return () => {
        if (chairImage?.startsWith('blob:')) {
            URL.revokeObjectURL(chairImage);
        }
    };
  }, [groupMembers.chair?.email]);

  // Load vice chair profile image
  useEffect(() => {
    if (groupMembers.viceChair?.email) {
        /**
         * Loads vice chair profile image from server with authenticated request
         * Creates blob URL for image display and handles errors gracefully
         */
        const loadImage = async () => {
            try {
                const profile = await apiHelper.getMemberProfileByEmail(groupMembers.viceChair.email);
                if (!profile?.photo) {
                    setViceChairImage(null);
                    return;
                }

                const authToken = await apiHelper.getAuthToken();
                const response = await fetch(`${process.env.REACT_APP_API_URL}graph/getMediaContent/profile-images/${profile.photo}`, {
                    headers: {
                        'Authorization': `Bearer ${authToken}`
                    }
                });

                if (!response.ok) {
                    setViceChairImage(null);
                    return;
                }

                const blob = await response.blob();
                const imageUrl = URL.createObjectURL(blob);
                setViceChairImage(imageUrl);
            } catch (error) {
                setViceChairImage(null);
            }
        };
        loadImage();
    }

    return () => {
        if (viceChairImage?.startsWith('blob:')) {
            URL.revokeObjectURL(viceChairImage);
        }
    };
  }, [groupMembers.viceChair?.email]);

  /**
   * Handles profile card click to open member profile modal
   * 
   * @param {Object} member - Member object containing profile information
   * @param {string|number} member.id - Unique member identifier
   */
  const handleProfileClick = (member) => {
    setProfileData({id: member.id });
    setShowProfileModal(true);
  }

  /**
   * Handles associated group card click for navigation
   * 
   * @param {Object} group - Working group object
   * @param {string|number} group.id - Unique group identifier for navigation
   */
  const handleGroupClick = (group) => {
    navigate(`/WorkingGroups/WorkingGroup/${group.id}`);
  };

  /**
   * Forces reload of member profile images
   * Cleans up existing blob URLs and fetches fresh images from server
   */
  const forceReloadImages = async () => {
    
    if (chairImage) {
        URL.revokeObjectURL(chairImage);
        setChairImage(null);
    }
    if (viceChairImage) {
        URL.revokeObjectURL(viceChairImage);
        setViceChairImage(null);
    }

    if (groupMembers.chair?.email) {
        try {
            const newChairImage = await apiHelper.getProfileImageByEmail(groupMembers.chair.email);
            setChairImage(newChairImage);
        } catch (error) {
            // Image loading failed - handled gracefully
        }
    }

    if (groupMembers.viceChair?.email) {
        try {
            const newViceChairImage = await apiHelper.getProfileImageByEmail(groupMembers.viceChair.email);
            setViceChairImage(newViceChairImage);
        } catch (error) {
            // Image loading failed - handled gracefully
        }
    }
  };

   return (
    <>
      <Toaster position="top-right" />
      <div className="sg-chair-container">
        {/* Chair Section - Always show if exists */}
        {groupMembers.chair && (
          <div className="sg-member-card" onClick={() => handleProfileClick(groupMembers.chair)}>
            {chairImage ? (
              <img
                src={chairImage}
                alt="Chair"
                className="sg-member-image"
              />
            ) : (
              <div className="wg-member-initials">
                {getInitials(groupMembers.chair.firstName, groupMembers.chair.lastName)}
              </div>
            )}
            <h4 className="sg-member-name">
              {`${groupMembers.chair.firstName} ${groupMembers.chair.lastName}`}
            </h4>
            <p className="sg-member-role">Chair</p>
          </div>
        )}

        {/* Associated Groups Section */}
        <div className="associated-groups-section">
                   
            <div className="associated-groups-grid">
              {associatedGroups.map(group => (
                <div 
                  key={group.id}
                  className="associated-group-card"
                  onClick={() => handleGroupClick(group)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleGroupClick(group);
                    }
                  }}
                >
                  <div className="card-content">
                    <span className={`group-type-badge ${group.workingGroupType.toLowerCase().replace(' ', '-')}`}>
                      {group.workingGroupType}
                    </span>
                    <h5 className="card-title">{group.workingGroupName}</h5>
                  </div>
                </div>
              ))}
            </div>
        
        </div>

        {/* Vice Chair Section - Always show if exists */}
        {groupMembers.viceChair && (
          <div className="sg-member-card" onClick={() => handleProfileClick(groupMembers.viceChair)}>
            {viceChairImage ? (
              <img
                src={viceChairImage}
                alt="Vice Chair"
                className="sg-member-image"
              />
            ) : (
              <div className="wg-member-initials">
                {getInitials(groupMembers.viceChair.firstName, groupMembers.viceChair.lastName)}
              </div>
            )}
            <h4 className="sg-member-name">
              {`${groupMembers.viceChair.firstName} ${groupMembers.viceChair.lastName}`}
            </h4>
            <p className="sg-member-role">Vice Chair</p>
          </div>
        )}
      </div>

      {showProfileModal && (
        <ProfileModal
          show={showProfileModal}
          onClose={() => setShowProfileModal(false)}
          small={true}
          profileData={profileData}
          onImageUpdate={forceReloadImages}
        />
      )}
    </>
  );
}

export default SGChairDetails;
